import { useForm, Controller } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
  Modal,
  Input,
  PrimaryButton,
  SecondaryButton,
  Typography,
  useSnackbar,
  SnackbarStatus,
} from '@weareopenr/component-library';
import { IExtension, TAuthenticationType } from 'src/api/extensions';
import { getAddons } from 'src/api/getAddons/getAddons';
import { useUpdateExtension } from 'src/hooks/useExtensions';
import { QueryKeys } from 'src/constants/QueryKeys';
import {
  buildAuthenticationPayload,
  IAuthFormValues,
} from 'src/utils/buildAuthenticationPayload/buildAuthenticationPayload';
import styles from './EditAuthModal.module.scss';

interface IEditAuthModalProps {
  open: boolean;
  onClose: () => void;
  brandId: string;
  extension: IExtension;
}

export const EditAuthModal = ({ open, onClose, brandId, extension }: IEditAuthModalProps) => {
  const updateMutation = useUpdateExtension(onClose);
  const { showSnackbar } = useSnackbar();
  const existingAuthType = extension.authentication?.type;

  // An extension can exist with no authentication at all — rows created outside the API
  // skip the AUTH_REQUIRED check. The addon still knows which credentials it accepts, so
  // fall back to that and let the user configure it rather than showing an empty form.
  const {
    data: addons,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [QueryKeys.ADDONS],
    queryFn: getAddons,
    enabled: open && !existingAuthType,
  });

  const addonAuthType = addons?.find(({ addon_id }) => addon_id === extension.addon.id)
    ?.authentication_types?.[0] as TAuthenticationType | undefined;

  const authType = existingAuthType ?? addonAuthType;
  const isAddingAuth = !existingAuthType;
  const isResolvingAuthType = isAddingAuth && isLoading;

  const { control, handleSubmit } = useForm<IAuthFormValues>({
    defaultValues: {
      username: '',
      password: '',
      access_token: '',
      contents: '',
    },
  });

  const onSubmit = (data: IAuthFormValues) => {
    const authentication = buildAuthenticationPayload(authType, data);

    if (!authentication) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: `We could not work out which credentials ${extension.addon.name} needs. Ask an openr engineer to check the addon's accepted authentication types.`,
      });
      return;
    }

    updateMutation.mutate({
      brandId,
      extensionId: extension.id,
      payload: { authentication },
    });
  };

  return (
    <Modal closeModal={onClose} open={open}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography size="xxlarge" weight="semi-bold" className={styles['heading']}>
          {isAddingAuth ? 'Add Authentication' : 'Edit Authentication'}
        </Typography>
        <Typography className={styles['subtitle']}>{extension.addon.name}</Typography>

        {isAddingAuth && !isResolvingAuthType && authType && (
          <Typography className={styles['warning']}>
            This extension has no credentials, so publishing to {extension.addon.name} fails. Enter
            the credentials below to fix it.
          </Typography>
        )}

        {isResolvingAuthType && (
          <Typography className={styles['subtitle']}>Loading addon details...</Typography>
        )}

        {!isResolvingAuthType && !authType && isError && (
          <Typography className={styles['warning']}>
            Could not load the details for {extension.addon.name}. Close this and try again.
          </Typography>
        )}

        {!isResolvingAuthType && !authType && !isError && (
          <Typography className={styles['warning']}>
            {extension.addon.name} does not list the credentials it accepts, so authentication
            cannot be set here. Ask an openr engineer to check the addon configuration.
          </Typography>
        )}

        {authType === 'BASIC' && (
          <>
            <div className={styles['form-group']}>
              <Controller
                name="username"
                control={control}
                rules={{ required: 'Username is required' }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id={field.name}
                    type="text"
                    label="Username"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>
            <div className={styles['form-group']}>
              <Controller
                name="password"
                control={control}
                rules={{ required: 'Password is required' }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id={field.name}
                    type="password"
                    label="Password"
                    placeholder="Enter new password"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>
          </>
        )}

        {authType === 'TOKEN' && (
          <div className={styles['form-group']}>
            <Controller
              name="access_token"
              control={control}
              rules={{ required: 'Access token is required' }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  id={field.name}
                  type="text"
                  label="Access Token"
                  placeholder="Enter new access token"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        )}

        {authType === 'CERTIFICATE' && (
          <div className={styles['form-group']}>
            <Controller
              name="contents"
              control={control}
              rules={{ required: 'Certificate contents is required' }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  id={field.name}
                  type="text"
                  label="Certificate Contents"
                  placeholder="Enter new certificate"
                  error={fieldState.error?.message}
                />
              )}
            />
          </div>
        )}

        <div className={styles['controls']}>
          <SecondaryButton onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            loading={updateMutation.isPending}
            disabled={!authType || isResolvingAuthType}
          >
            {isAddingAuth ? 'Save Authentication' : 'Save Changes'}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
};
